import { useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
// Utils
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { isNil, sortBy } from 'lodash';
// Components
import {
  Button, FilterBlock, DropdownList, DubbleSideButton,
} from '@repo/zolintra-ui';

// Hooks
import { useTranslation } from 'react-i18next';
import usePost from '../../../hooks/domain/usePost';
import useSearchParams from '../../../hooks/useSearchParams';
import useResources from '../../../hooks/domain/useResources';
import useMonthSelector from '../../../hooks/useMonthSelector';
// Templates
import LeaveTable from './tables/LeaveTable';
// Services
import { usePostsListQuery } from '../../../services/post/postQueries';
import { useResourcesListQuery, useResourcesStaffListQuery } from '../../../services/resource/resourceQueries';
// Constants
import { VIEW_DOMAIN } from '../../../constants';
// Styles
import styles from './ListSubView.module.css';

import { useAppContext } from '../../../store/AppContext';
import hasPermission, { PERMISSIONS } from '../../../permissions/hasPermission';
import useLeavePost from '../../../hooks/domain/useLeavePost';
import useListSubView from './hook/useListSubView';

const dateFnsLocales = {
  fr,
};

/**
 * @component
 * @returns {JSX.Element} The rendered ListSubViewLeave component.
 */
function ListSubViewLeave() {
  // Hooks
  const [{ user }] = useAppContext();
  const { getSearchParams } = useSearchParams();
  const { t } = useTranslation();
  const {
    getPostStatusList,
    getSlotsIdFromMonth,
  } = usePost();
  const { getTypesOptions } = useLeavePost();
  const { transformResourcesListToOptions, getStaffResourcesListWithUserInfo } = useResources();
  const { handleMonthSelectorClick, setMonthFilterOnToday } = useMonthSelector();

  // States
  const [isFilterDisplayed, setIsFilterDisplayed] = useState(false);
  const {
    year = new Date().getFullYear().toString(),
    month = new Date().getMonth().toString(),
    status,
    type: leaveType,
    resourceId,
  } = getSearchParams();
  const viewMonthSlotsId = getSlotsIdFromMonth({ month: Number(month), year });

  const hasAnyPostAndResourcePermission = (
    hasPermission(PERMISSIONS.GET_ANY_RESOURCE, user.prm)
    && hasPermission(PERMISSIONS.GET_ANY_POST, user.prm)
    && hasPermission(PERMISSIONS.IDENTITY_ACCESS_GET_ANY_RESOURCE, user.prm)
  );

  // Constants
  const statusOptions = getPostStatusList().map((option) => ({
    value: option.value,
    label: t(option.key),
  }));
  const typesOptions = getTypesOptions(true).map((option) => ({
    ...option,
    label: t(option.label),
  }));

  // API Calls
  const resourcesListQuery = useResourcesListQuery({
    enabled: hasAnyPostAndResourcePermission,
  });
  const resourcesStaffListQuery = useResourcesStaffListQuery({
    enabled: hasAnyPostAndResourcePermission,
  });
  const resourcesListOptions = transformResourcesListToOptions(
    getStaffResourcesListWithUserInfo(
      resourcesStaffListQuery.data,
      resourcesListQuery.data,
    ),
  );
  const postListQuery = usePostsListQuery(
    { year },
    {
      includedStartSlotId: viewMonthSlotsId.startSlotId,
      excludedEndSlotId: viewMonthSlotsId.endSlotId + 1,
      status,
      type: VIEW_DOMAIN.LEAVE,
      resourceId: user.id,
      businessUnit: null,
    },
    {
      refetchOnWindowFocus: true,
      enabled: !!user.id,
      placeholderData: (prev) => prev,
    },
  );

  const {
    acceptPostMutation,
    refusePostMutation,
    handleFilterChange,
    handleResetFilterClick,
  } = useListSubView(postListQuery);

  const availableFilters = [status, leaveType, resourceId];
  const activeFiltersCount = useMemo(() => availableFilters.reduce((acc, filter) => (
    acc + (!isNil(filter) ? 1 : 0)
  ), 0), [availableFilters]);

  return (
    <>
      <div className={styles['view-config']}>
        <div className={styles['view-switches']}>
          <DubbleSideButton
            onLeftClick={() => handleMonthSelectorClick('previous')}
            onRightClick={() => handleMonthSelectorClick('next')}
            label={format(new Date(+year, Number(month)), 'MMMM yyyy', { locale: dateFnsLocales.fr })}
            className="margin-left-m"
          />

          <Button
            onClick={setMonthFilterOnToday}
            color="transparent"
            className="margin-left-m margin-right-m"
          >
            <span className="font-bold">
              {t('remoteWorkingAndLeave.today')}
            </span>
          </Button>
        </div>

        <Button
          onClick={() => setIsFilterDisplayed(true)}
          color="transparent"
          className={activeFiltersCount > 0 ? styles['has-filters'] : ''}
          shape="rectangle"
        >
          <span className="font-bold">
            {t('remoteWorkingAndLeave.filtersBtnLabel')}
          </span>

          {activeFiltersCount > 0 ? (
            <span className={styles['has-filters-count']}>{activeFiltersCount}</span>
          ) : null}
        </Button>
      </div>

      <div className={styles.content}>
        <ErrorBoundary fallback={<>{t('common.wrong')}</>}>
          <LeaveTable
            data={sortBy(postListQuery.data?.data || [], 'includedStartSlotId')}
            isPostListFetching={postListQuery.isFetching}
            asyncActions={{
              acceptPost: (params) => acceptPostMutation.mutate(params),
              refusePost: (params) => refusePostMutation.mutate(params),
            }}
          />
        </ErrorBoundary>
      </div>

      <FilterBlock
        onClose={() => setIsFilterDisplayed(false)}
        closeLabel="Masquer les filtres"
        isOpen={isFilterDisplayed}
      >
        <div className={styles['content-filters']}>
          <DropdownList
            label={t('remoteWorkingAndLeave.listSubView.filters.status.label')}
            placeholder={t('remoteWorkingAndLeave.listSubView.filters.status.placeholder')}
            options={statusOptions}
            noOptionsMessage={t('common.noOptionsMessage')}
            value={statusOptions.find((option) => option.value === status) || null}
            onChange={(option) => handleFilterChange('status', option ? option.value : null)}
            isInsideDialog
            isClearable
          />

          <DropdownList
            label={t('remoteWorkingAndLeave.listSubView.filters.leaveTypes.label')}
            placeholder={t('remoteWorkingAndLeave.listSubView.filters.leaveTypes.placeholder')}
            options={typesOptions}
            noOptionsMessage={t('common.noOptionsMessage')}
            value={typesOptions.find((option) => option.value === leaveType) || null}
            onChange={(option) => handleFilterChange('type', option ? option.value : null)}
            isInsideDialog
            isClearable
          />

          {hasAnyPostAndResourcePermission ? (
            <DropdownList
              label={t('remoteWorkingAndLeave.listSubView.filters.resource.label')}
              placeholder={t('remoteWorkingAndLeave.listSubView.filters.resource.placeholder')}
              options={resourcesListOptions}
              noOptionsMessage={t('common.noOptionsMessage')}
              value={resourcesListOptions.find((option) => option.value === resourceId) || null}
              onChange={(option) => handleFilterChange('resourceId', option ? option.value : null)}
              isInsideDialog
              isClearable
            />
          ) : null}

          <div className={styles.reset}>
            <Button
              onClick={handleResetFilterClick}
              color="transparent"
            >
              <span className="font-bold">
                {t('remoteWorkingAndLeave.listSubView.filters.resetFilterLabel')}
              </span>
            </Button>
          </div>
        </div>
      </FilterBlock>
    </>
  );
}

ListSubViewLeave.propTypes = {};

export default ListSubViewLeave;
